import React, { useState } from "react";
import { Search, Download, Bot, User } from "lucide-react";

const ChatHistory = () => {
  const [conversations] = useState([
    {
      id: 1,
      customerName: "Michael Brown",
      type: "Website Chat",
      timestamp: "2024-03-15 14:30",
      status: "Qualified Lead",
      messages: [
        {
          id: 1,
          type: "bot",
          text: "Hi! Welcome to Collings Real Estate. How can I assist you today?",
        },
        {
          id: 2,
          type: "user",
          text: "I'm interested in selling my property in Melbourne.",
        },
        {
          id: 3,
          type: "bot",
          text: "Great! Could you tell me more about your property and when you're planning to sell?",
        },
        {
          id: 4,
          type: "user",
          text: "It's a 3-bedroom house in South Yarra. Looking to sell within 2 months.",
        },
      ],
      aiSummary:
        "Property owner interested in selling 3BR house in South Yarra. Timeline: 2 months. High intent lead.",
      leadScore: 85,
      assignedTo: "Sarah Johnson",
    },
    // Add more sample conversations
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-light">Chat History</h2>
          <p className="text-gray-600">
            Review AI chat interactions and lead generation
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 px-4 py-2 pr-10 text-sm border border-gray-200 rounded-xl 
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary 
                       bg-gray-50/50 placeholder-gray-400"
              style={{ "--tw-ring-color": brandColors.secondary }}
            />
            <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl 
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            style={{ "--tw-ring-color": brandColors.secondary }}
          >
            <option value="all">All Chats</option>
            <option value="website">Website Chat</option>
            <option value="ai-call">AI Call</option>
            <option value="qualified">Qualified Leads</option>
          </select>
        </div>
      </div>

      {/* Conversations List */}
      <div className="space-y-4">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all duration-300"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {conversation.customerName}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-sm text-gray-500">
                    {conversation.timestamp}
                  </span>
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {conversation.type}
                  </span>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                    {conversation.status}
                  </span>
                </div>
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
                <Download className="w-4 h-4" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="space-y-3 mb-4">
              {conversation.messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-2 ${
                    message.type === "user"
                      ? "flex-row-reverse space-x-reverse"
                      : ""
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      message.type === "bot" ? "bg-blue-100" : "bg-gray-100"
                    }`}
                  >
                    {message.type === "bot" ? (
                      <Bot className="w-4 h-4 text-blue-600" />
                    ) : (
                      <User className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <div
                    className={`px-4 py-2 rounded-xl max-w-md ${
                      message.type === "bot"
                        ? "bg-blue-50 text-blue-900"
                        : "bg-gray-50 text-gray-900"
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Summary */}
            <div className="bg-purple-50 rounded-xl p-4 mb-4">
              <div className="flex items-start space-x-2">
                <Bot className="w-5 h-5 text-purple-600 mt-1" />
                <div>
                  <p className="text-sm font-medium text-purple-900 mb-1">
                    AI Summary
                  </p>
                  <p className="text-sm text-purple-800">
                    {conversation.aiSummary}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-sm text-gray-500">Lead Score</p>
                  <p className="text-lg font-medium text-gray-900">
                    {conversation.leadScore}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Assigned To</p>
                  <p className="text-sm font-medium text-gray-900">
                    {conversation.assignedTo}
                  </p>
                </div>
              </div>
              <button
                className="px-4 py-2 text-sm font-medium rounded-xl text-white transition-all duration-200"
                style={{ backgroundColor: brandColors.secondary }}
              >
                View Full Conversation
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatHistory;
